import React, { useState, useContext, useEffect, useRef } from 'react';
import { Alert, Button, Container, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import SessionContext from "SessionContext";
import { ProgressLoaderSimple } from 'partials/progress-loader';
import CensusDestinationSync from 'partials/census-sync/census-destination-sync';

const OrderFinalizeExternalDestination = ({selectedDestinations, onCensusData, onExit, order, onMappingStatusChange} ) => {
    const session = useContext(SessionContext);
    const [showSyncConfig, setShowSyncConfig] = useState(false);
    const [syncSetupComplete, setSyncSetupComplete] = useState(false);
    const [showFailureModal, setShowFailureModal] = useState(false);
    const [updatedOrder, setUpdatedOrder] = useState(order); // Store updated order from polling
    const abortRef = useRef(false);
    const pollTimeoutRef = useRef(null);

    // Abort polling and clear timeouts on unmount
    useEffect(() => {
        return () => {
            abortRef.current = true;
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
            }
        };
    }, []);

    async function pollForDestSetup(apiCall, orderId, interval = 2000, timeout = 60000) {
        return new Promise((resolve, reject) => {
            const start = Date.now();
            const poll = async () => {
                if (abortRef.current) {
                    reject(new Error("Polling aborted"));
                    return;
                }
                // Check timeout before making the API call
                if (Date.now() - start > timeout) {
                    reject(new Error("Polling timed out"));
                    return;
                }
                try {
                    const response = await apiCall(orderId);
                    if (abortRef.current) {
                        reject(new Error("Polling aborted"));
                        return;
                    }
                    if (response?.outgoing_order_table_created && response?.census_refresh_complete && response?.census_source_id) {
                        resolve(response);
                    } else {
                        pollTimeoutRef.current = setTimeout(poll, interval);
                    }
                } catch (err) {
                    reject(err);
                }
            };
            poll();
        });
    }

    const pollFunction = async (orderId) => {
        const res = await session.query('order/get', { id: orderId })
        return res;
    };

    const initialSyncSetup = async (_order) => {
        if (!_order?.outgoing_order_table_created || !_order?.census_refresh_complete || !_order?.census_source_id) {
            session.command('census/create-output-table', _order)
            .then((response) => {
                if (abortRef.current) return;
                pollForDestSetup(pollFunction, order.id, 2000, window.census_sync_setup_wait_time || 60000)
                    .then((res) => {
                        if (abortRef.current) return;
                        if (!res.reason) {
                            // Store the updated order with census_source_id
                            setUpdatedOrder(res);
                            setSyncSetupComplete(true);
                        } else {
                            setShowFailureModal(true);
                        }
                    })
                    .catch(reason => {
                      if (abortRef.current) return;
                      setShowFailureModal(true);
                    });
            })
            .catch(error => {
                if (abortRef.current) return;
                console.log("Error during initial sync setup:", error);
                setShowSyncConfig(false);
            });
        }else {
           setSyncSetupComplete(true);
        }
    };

    const handleOnExit = (data) => {
      const created = data?.status === "created";
      onExit(created);
      // Only hide config on successful sync creation.
      // On cancel, the parent controls unmount via showExtDest
      // (which may first show a confirmation modal for unsaved mapping changes).
      if (created) {
        setShowSyncConfig(false);
      }
    };
    
    const toggleFailureModal = () => { 
      const current = showFailureModal;
      setShowFailureModal(!showFailureModal); 
      current && onExit(false);
    }
    // INIT SETUP
    useEffect(() => {
      // Calls API to create Tables
      initialSyncSetup(order);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (syncSetupComplete && selectedDestinations.length) {
          setShowSyncConfig(true);
        }
    // eslint-disable-next-line
    }, [syncSetupComplete]);
    
    if (!syncSetupComplete) {
        return (
          <>
            <StatusLoading />
            <CensusSyncFailureModal isOpen={showFailureModal} toggle={toggleFailureModal} />
          </>
        );
    } else {
        return (
          <>
            <Container fluid className="order-mapping" style={{ minHeight: '100vh' }}>
              {showSyncConfig &&
                <CensusDestinationSync
                  selectedDestination={selectedDestinations[0]}
                  sourceId={updatedOrder.census_source_id}
                  destinationId={selectedDestinations[0]?.census_destination_id}
                  order={updatedOrder}
                  onExit={handleOnExit}
                  onMappingStatusChange={onMappingStatusChange}
                />
              }
            </Container>
            <CensusSyncFailureModal isOpen={showFailureModal} toggle={toggleFailureModal} />
          </>
        );
    }
};

export default OrderFinalizeExternalDestination;

const StatusLoading = () => {
  return (
    <section className="order-mapping-status">
      <div className="status-loading">
        <div className="loader-wrapper"><ProgressLoaderSimple /></div>
        <div className="text-wrapper">Preparing for mapping the destination fields...</div>
      </div>
    </section>
  );
}

const CensusSyncFailureModal = ({isOpen, toggle}) => {
  return (
    <Modal centered backdrop size="lg" isOpen={isOpen}
    >
      <ModalHeader toggle={toggle}>
        <i className="bi bi-exclamation-triangle-fill"></i>Destination Sync Mapping Failure
      </ModalHeader>
      <ModalBody>
        <div>
          <p>
            There has been an error trying to map the fields for this external destination. 
          </p>
          <Alert color='warning'>
             {/* eslint-disable-next-line */}
            <div>
            <p><i className='bi bi-lightbulb'></i> <strong>Please try finalizing your order again.</strong></p>
              <p>If the issue persists contact support at <a className='link-lc' href="mailto:audiencemaker@speedeondata.com">audiencemaker@speedeondata.com</a></p>
            </div>
          </Alert>
        </div>

      </ModalBody>
      <ModalFooter>
        <Button onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
